import React, { useCallback, useMemo, useState } from 'react';
import { Popover, Tooltip } from '@mantine/core';
import { useEventListener } from 'ahooks';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { EditableText } from '@shakkerai/pro-components';
import { updateGenerateParams } from '@shakkerai/pro-store';
import { MIN_GENERATOR_SIZE, MAX_GENERATOR_SIZE } from '@shakkerai/pro-constants';
import { presetFrameSizes } from '@shakkerai/pro-constants';

import '../style.scss';

const labelStyle = { label: { fontSize: '12px' } };

const iconCustomSize = (
  <div className="flex items-end justify-start w-3 h-3 border border-[#2d2d2d] rounded-[1px]">
    <div className="w-[6px] h-[7px] bg-[#8c8c8c18] border-r border-t border-[#2d2d2d] rounded-[1px]" />
  </div>
);

export const GenerateSize = React.memo(({ disabled, generator, onChange }) => {
  const handleChange = useCallback(
    (updatedData) => {
      if (updatedData.lockRatio === false) {
        updatedData.isFixedRatio = false;
      }

      updateGenerateParams(generator.id, updatedData);
      onChange && onChange(updatedData);
    },
    [generator.id, onChange],
  );

  return (
    <div className="flex items-center justify-between mt-3">
      <FrameSize disabled={disabled} value={generator.params} onChange={handleChange} />
      <SizeInputGroup disabled={disabled} value={generator.params} onChange={handleChange} />
    </div>
  );
});

export const SizeInputGroup = React.memo(
  ({ disabled, hideLockButton, maxSize = MAX_GENERATOR_SIZE, value, lockRatio, onChange }) => {
    const ratio = value.width / value.height;
    lockRatio = typeof lockRatio === 'boolean' ? lockRatio : value.lockRatio || false;

    const handleChange = useCallback(
      (value, name) => {
        value = parseInt(value, 10);

        if (isNaN(value)) return;
        value = Math.max(MIN_GENERATOR_SIZE, Math.min(maxSize, value));

        const changedProps = { [name]: value };

        if (lockRatio) {
          if (name === 'width') {
            //
            changedProps.height = value / ratio;
            if (changedProps.height > maxSize) {
              changedProps.height = maxSize;
              changedProps.width = changedProps.height * ratio;
            } else if (changedProps.height < MIN_GENERATOR_SIZE) {
              changedProps.height = MIN_GENERATOR_SIZE;
              changedProps.width = changedProps.height * ratio;
            }
          } else if (name === 'height') {
            changedProps.width = value * ratio;
            if (changedProps.width > maxSize) {
              changedProps.width = maxSize;
              changedProps.height = changedProps.width / ratio;
            } else if (changedProps.width < MIN_GENERATOR_SIZE) {
              changedProps.width = MIN_GENERATOR_SIZE;
              changedProps.height = changedProps.width / ratio;
            }
          }

          changedProps.width = Math.max(MIN_GENERATOR_SIZE, Math.min(maxSize, changedProps.width));
          changedProps.height = Math.max(MIN_GENERATOR_SIZE, Math.min(maxSize, changedProps.height));
        }

        onChange(changedProps);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [ratio, onChange, lockRatio],
    );

    if (disabled) {
      return (
        <div className="flex items-center relative leading-3">
          <div className="h-6 flex items-center">
            <div className="text-[#909090] w-[25px] text-[11px] shrink-0">W</div>
            <span className="text-[#999] text-mini">{value.width}</span>
          </div>
          <div className="h-6 flex items-center ml-4">
            <div className="text-[#909090] w-[25px] text-[11px] shrink-0">H</div>
            <span className="text-[#999] text-mini">{value.height}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between relative leading-3">
        <div className="h-6 flex items-center">
          <div className="text-[#909090] translate-y-px text-[11px] shrink-0">W</div>
          <EditableText
            className="text-center w-[45px]"
            styles={labelStyle}
            name="width"
            value={Math.round(value.width)}
            onChange={handleChange}
          />
        </div>
        <Tooltip position="bottom-end" withArrow className="scale-90" label={translate('lock_ratio')}>
          <button
            disabled={hideLockButton}
            onClick={() => onChange({ lockRatio: !lockRatio })}
            className={`flex items-center justify-center w-5 h-5 disabled:opacity-50 ml-[8px] mr-[12px] rounded-xs ${
              lockRatio ? 'bg-[#F4F6F8] text-[#005848]' : 'text-[#7f7f7f]'
            }`}
          >
            <Icon fontSize={12} className="shk-kuangjia-suo" color="inherit" />
          </button>
        </Tooltip>
        <div className="h-6 flex items-center">
          <div className="text-[#909090] translate-y-px text-[11px] shrink-0">H</div>
          <EditableText
            className="text-center w-[45px]"
            styles={labelStyle}
            name="height"
            value={Math.round(value.height)}
            onChange={handleChange}
          />
        </div>
      </div>
    );
  },
);

export const FrameSize = React.memo(({ disabled, value, onChange }) => {
  const [opened, setOpend] = useState(false);

  const entryIcon = useMemo(() => {
    return (
      <div className="flex items-center justify-center w-[14px] h-[14px] mr-1">
        {value?.isFixedRatio ? (
          <div
            style={{ width: value?.icon?.[0] || 12, height: value?.icon?.[1] || 12 }}
            className="bg-[#8c8c8c18] border border-[#2d2d2d] rounded-[1px]"
          />
        ) : (
          iconCustomSize
        )}
      </div>
    );
  }, [value]);

  const entryLabel = useMemo(() => {
    return value.isFixedRatio
      ? presetFrameSizes.find((item) => {
          return item.width === value.width && item.height === value.height;
        })?.label || translate('generator_size_custom')
      : translate('generator_size_custom');
  }, [value.isFixedRatio, value.width, value.height]);

  const handleChange = useCallback(
    (value) => {
      onChange(value);
    },
    [onChange],
  );

  const handleGlobalClick = useCallback((event) => {
    if (!event.target.closest('[data-role="frame-size-trigger"]')) {
      setOpend(false);
    }
  }, []);

  useEventListener('click', handleGlobalClick);

  if (disabled) {
    return (
      <div className="flex self-start items-center h-6 text-xs text-[#2d2d2d]">
        {entryIcon}
        <span className="translate-y-px mr-2 font-grotesk">{entryLabel}</span>
        {/* <Icon fontSize={10} className="icon-filled_down" color="#999" /> */}
      </div>
    );
  }

  return (
    <Popover position="bottom-start" opened={opened}>
      <Popover.Target>
        <button
          disabled={disabled}
          data-role="frame-size-trigger"
          onClick={() => setOpend((opened) => !opened)}
          className="flex self-start items-center h-6 text-xs text-[#2d2d2d]"
        >
          {entryIcon}
          <span className="translate-y-px mr-2 font-grotesk">{entryLabel}</span>
          <Icon fontSize={18} className="shk-kuangjia-xiala" color="#999" />
        </button>
      </Popover.Target>
      <Popover.Dropdown className="custom-popover">
        <div className="flex flex-col w-28 py-1 bg-white rounded-[2px]">
          {presetFrameSizes.map((item, index) => {
            const { label, icon, isFixedRatio, width, height } = item;
            const checked =
              (!item.isFixedRatio && !value.isFixedRatio) ||
              (value.isFixedRatio && value.width === width && value.height === height);

            return (
              <button
                key={index}
                disabled={checked}
                className="group flex items-center h-6 px-2 hover:bg-[#f4f6f8] disabled:bg-primary"
                onClick={() =>
                  handleChange(
                    !isFixedRatio
                      ? { isFixedRatio, lockRatio: false }
                      : { isFixedRatio, lockRatio: true, width, height, icon },
                  )
                }
              >
                <div className="flex items-center justify-center w-4 h-6">
                  {isFixedRatio ? (
                    <div
                      style={{ width: icon[0], height: icon[1] }}
                      className="bg-[#8c8c8c18] border border-[#2d2d2d] rounded-[1px]"
                    />
                  ) : (
                    iconCustomSize
                  )}
                </div>
                <span className="ml-2 text-xs text-[#2d2d2d] font-semibold">{label}</span>
              </button>
            );
          })}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
});
